import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { employeeLookup } from '../../../components/performance/PmAnalyticsFilterBar';
import {
  ASSIGNEE_KINDS, ASSIGNEE_ROLES, EMPTY_TASK_FILTERS, OVERDUE_BANDS, OVERDUE_VALUES, TASK_STATUSES,
} from '../../../components/performance/processHealthLinks';
import { STAGE_ORDER, stageLabel, statusLabel } from '../config/tasks';

// Spec §7.3, §19/1 — Görevler sekmesinin süzgeçleri: aşama, görev durumu, gecikme, sorumlu, sorumlu türü, sorumlu rolü, son tarih
// aralığı, gecikme bandı, önümüzdeki N gün. Değer URL'dedir (çağıranın elinde); çekmece yalnız onApply çağırır. Başlangıç değeri
// açılışta donar: sayfa yeniden çizildikçe yeni nesne gelseydi GenericFormDrawer formu sıfırlar, seçimler silinirdi (Plan 3 Önemli 3).
const staticLookup = (ids, labelOf) => ({
  minChars: 0,
  onSearch: async () => ids.map((id) => ({ id, name: labelOf(id) })),
  onGetById: async (id) => ({ id, name: labelOf(id) }),
});

export function toFormValues(f) {
  return {
    stage: f.stage,
    statuses: f.statuses,
    overdue: f.overdue,
    assigneeEmployeeId: f.assigneeEmployeeId,
    assigneeKind: f.assigneeKind,
    roles: f.roles,
    dueFrom: f.dueFrom || '',
    dueTo: f.dueTo || '',
    bands: f.bands,
    dueWithinDays: f.dueWithinDays ? String(f.dueWithinDays) : '',
  };
}

const day = (v) => (v ? String(v).slice(0, 10) : null);

export function fromFormValues(v) {
  const within = Number(v.dueWithinDays);
  return {
    ...EMPTY_TASK_FILTERS,
    stage: v.stage || null,
    statuses: (v.statuses || []).filter((s) => TASK_STATUSES.includes(s)),
    overdue: OVERDUE_VALUES.includes(v.overdue) ? v.overdue : null,
    assigneeEmployeeId: v.assigneeEmployeeId || null,
    assigneeKind: ASSIGNEE_KINDS.includes(v.assigneeKind) ? v.assigneeKind : null,
    roles: (v.roles || []).filter((r) => ASSIGNEE_ROLES.includes(r)),
    dueFrom: day(v.dueFrom),
    dueTo: day(v.dueTo),
    bands: (v.bands || []).filter((b) => OVERDUE_BANDS.includes(b)),
    dueWithinDays: Number.isInteger(within) && within >= 1 && within <= 30 ? within : null,
  };
}

export default function TaskFilterDrawer({ open, value, onClose, onApply }) {
  const { t } = useTranslation();
  const [initial, setInitial] = useState(null);
  useEffect(() => {
    setInitial(open ? toFormValues({ ...EMPTY_TASK_FILTERS, ...value }) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const f = (key) => t(`performance.processHealth.filter.${key}`);
  const schema = [
    {
      name: 'stage', label: f('stage'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name',
      options: STAGE_ORDER.map((id) => ({ id, name: stageLabel(t, id) })),
    },
    { name: 'statuses', label: f('statuses'), type: 'asyncSelect', isMulti: true, grid: 12, ...staticLookup(TASK_STATUSES, (id) => statusLabel(t, id)) },
    {
      name: 'overdue', label: f('overdue'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name',
      options: OVERDUE_VALUES.map((id) => ({ id, name: t(`performance.processHealth.filter.overdueValue.${id}`) })),
    },
    { name: 'assigneeEmployeeId', label: f('assigneeEmployeeId'), type: 'asyncSelect', grid: 12, ...employeeLookup },
    {
      name: 'assigneeKind', label: f('assigneeKind'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name',
      options: ASSIGNEE_KINDS.map((id) => ({ id, name: t(`performance.processHealth.assigneeKind.${id}`) })),
    },
    {
      name: 'roles', label: f('roles'), type: 'asyncSelect', isMulti: true, grid: 12,
      ...staticLookup(ASSIGNEE_ROLES, (id) => t(`performance.reminder.role.${id}`, { defaultValue: id })),
    },
    { name: 'dueFrom', label: f('dueFrom'), type: 'date', grid: 6 },
    { name: 'dueTo', label: f('dueTo'), type: 'date', grid: 6 },
    { name: 'bands', label: f('bands'), type: 'asyncSelect', isMulti: true, grid: 12, ...staticLookup(OVERDUE_BANDS, (id) => t(`performance.processHealth.band.${id}`)) },
    { name: 'dueWithinDays', label: f('dueWithinDays'), type: 'number', grid: 12, min: 1, max: 30 },
  ];

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={t('performance.processHealth.filter.title')}
      schema={schema}
      initialValues={initial || undefined}
      submitLabel={t('performance.operations.filter.apply')}
      onSave={async (values) => {
        onApply(fromFormValues(values));
        return true;
      }}
    />
  );
}
